// Core Modules
import { Component, OnInit, OnChanges, Input } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';

// Application Services
import { ClientService } from 'app/services/client.service';
import { ToastrService } from 'ngx-toastr';
import * as _ from 'lodash';
import { SurvaleCommonComponent } from '../../../../common/survale-common.component';
import { AuthenticationService } from '../../../../services/authentication.service';

/**
 * Client classification component
 */
@Component({
    selector: 'app-client-classification',
    templateUrl: './client-classification.component.html',
    styleUrls: ['./client-classification.component.scss'],
    standalone: false
})
export class ClientClassificationComponent extends SurvaleCommonComponent implements OnInit, OnChanges {

  @Input() settings;
  public primaryIndustries: any;
  public companyRevenue: any;
  public globalRevenue: any;
  public employeePopulation: any;
  public globalEmployeePopulation: any;
  public newHires: any;
  public globalNewHires: any;
  public recruitingTeamSize: any;
  public recruitingTeamStructure: any;
  public clientClassificationForm: UntypedFormGroup;
  public otherSettingsForm: UntypedFormGroup;
  public socialMediaForm: UntypedFormGroup;
  public clientLogoForm: UntypedFormGroup;
  public clientTypeForm: UntypedFormGroup;
  public childClients: any[];
  public averageJobReqs: any;

  /**
   * Life cycle method
   * @param authenticationService: AuthenticationService
   * @param clientService ClientService
   * @param fb FormBuilder
   * @param toaster toaster
   */
  constructor(
    public authenticationService: AuthenticationService,
    private clientService: ClientService,
    private fb: UntypedFormBuilder,
    private toaster: ToastrService
  ) {
    super(authenticationService);
  }

  /**
   * Life cycle init method
   */
  ngOnInit(): void {    
    // Initialize form
    this.initializeForm();

    this.primaryIndustries = this.clientService.getPrimaryIndustries();
    this.companyRevenue = this.clientService.getCompanyRevenue();
    this.globalRevenue = this.clientService.getGlobalRevenue();
    this.employeePopulation = this.clientService.getEmployeePopulation();
    this.globalEmployeePopulation = this.clientService.getGlobalEmployeePopulation();
    this.averageJobReqs = this.clientService.getAverageJobReqs();
    this.newHires = this.clientService.getCompanyNewHires();
    this.globalNewHires = this.clientService.getGlobalNewHires();
    this.recruitingTeamSize = this.clientService.getRecruitingSize();
    this.recruitingTeamStructure = this.clientService.getRecruitingTeamStructure();
    this.setChildClients();
  }

  setChildClients(): void {
    this.clientService.getChildClients().subscribe(data => {
      this.childClients = _.map(
        _.filter(data.body, c => c.clientId !== this.currentUser.clientId &&
          ( _.isNil(c.mainClientId) || c.mainClientId === 0 || (-1 * c.mainClientId) === this.currentUser.clientId)
        ),
        c => _.pick(c, ['clientId', 'name']));
      this.childClients = _.sortBy(this.childClients, c => c.name);
      // console.log('this.childClients', this.childClients, this.currentUser);
    }, (error): any => {
      this.toaster.error('Error loading sub clients');
    });
  }

  /**
   * Initialize Form
   */
  initializeForm(): void {
    this.socialMediaForm = this.fb.group({
      facebook: new UntypedFormControl(),
      twitter: new UntypedFormControl(),
      linkedIn: new UntypedFormControl(),
      instagram: new UntypedFormControl(),
      youtube: new UntypedFormControl(),
      glassdoor: new UntypedFormControl(),
      indeed: new UntypedFormControl(),
      // pinterest: new FormControl(),
      // googlePlus: new FormControl(),
      // snapchat: new FormControl(),
      // tiktok: new FormControl(),
      // other: new FormControl(),
    });

    this.clientLogoForm = this.fb.group({
      companyLogo: new UntypedFormControl(),
      backgroundImage: new UntypedFormControl(),
    });

    this.clientClassificationForm = this.fb.group({
      primaryIndustry: new UntypedFormControl([]),
      companyRevenue: new UntypedFormControl(),
      globalRevenue: new UntypedFormControl(),
      employeePopulation: new UntypedFormControl(),
      globalEmployeePopulation: new UntypedFormControl(),
      newHires: new UntypedFormControl(),
      globalNewHires: new UntypedFormControl(),
      recruitingTeamSize: new UntypedFormControl(),
      recruitingStructure: new UntypedFormControl(),
      averageJobReqs: new UntypedFormControl(),
    });


    this.otherSettingsForm = this.fb.group({
      privacyPolicyUrl: new UntypedFormControl(),
      phone: new UntypedFormControl(),
      email: new UntypedFormControl(),
      clientName: new UntypedFormControl(),
      firstName: new UntypedFormControl(),
      lastName: new UntypedFormControl(),
      jobTitle: new UntypedFormControl(),
      promoCode: new UntypedFormControl(),
      clientAddress: new UntypedFormControl(),
      clientCity: new UntypedFormControl(),
      clientState: new UntypedFormControl(),
      clientZip: new UntypedFormControl(),
    });

    if (this.isSurvaleAdmin) {
      this.clientTypeForm = this.fb.group({
        clientType: new UntypedFormControl(),
        testEmailAddresses: new UntypedFormControl(),
        mainClientId: new UntypedFormControl(),
      });
      this.clientTypeForm.patchValue({clientType: 'production'});
    }
  }


  disableNonSandboxFields(): boolean {
    return this.clientTypeForm.controls.clientType.value === 'production';
  }

  /**
   * Set form value when received from parent
   */
  ngOnChanges(): void {

    console.log('this.settings', this.settings);
    // primary industry is being made multiple select. for backward compatibility
    if (this.settings && !_.isArray(this.settings?.clientClassification?.primaryIndustry)) {
      this.settings.clientClassification.primaryIndustry = [this.settings.clientClassification.primaryIndustry];
    }

    if (this.clientClassificationForm && this.settings && this.settings.clientClassification) {
      this.clientClassificationForm.patchValue(this.settings.clientClassification);
    }
    if (this.socialMediaForm && this.settings && this.settings.socialMediaUrls) {
      this.socialMediaForm.patchValue(this.settings.socialMediaUrls);
    }
    if (this.clientLogoForm && this.settings && this.settings.logos) {
      this.clientLogoForm.patchValue(this.settings.logos);
    }
    if (this.otherSettingsForm && this.settings && this.settings.otherSettings) {
      this.otherSettingsForm.patchValue(this.settings.otherSettings);
    }
    
    if (this.isSurvaleAdmin) {
      if (this.clientTypeForm && this.settings && this.settings.clientTypeSettings) {
        this.clientTypeForm.patchValue(this.settings.clientTypeSettings);
        this.setChildClients();
      }
    }
  }
}
